import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';
import { PHI, buildTiling, subdivide, withinRadius, arcsOf, sideFlags, flowers, orientationBin } from './lib/penrose-tiling.js';

// Contrat du rig partagé : interdit. Le néon est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'penrose',
  family: 'geometric',
  danceability: 0.85,
  energyLevel: 0.78,
  bpmMin: 115,
  bpmMax: 150,
  movement: 0.70,
  tonality: 0.55,
  prefers: { gridStrength: [0.5, 1.0] },
  enabled: true
};

// PENROSE : un pavage de Penrose (losanges fin et épais) en néon, zoomé à
// l'infini. Le pavage est auto-similaire : chaque losange se subdivise en
// losanges plus petits du même pavage (déflation), on plonge sans fin dans
// un motif qui ne se répète jamais, avec la symétrie d'ordre 5 au centre de
// l'écran (R19). Techno, trance, progressive, psytrance. Règles R1 à R19,
// preset party de vdjLook, horloge de scène partagée (« R20 », lib/scene-clock.js).
//
// LE PAVAGE (lib/penrose-tiling.js) : des triangles de Robinson (moitiés de
// losanges), déflatés sur le CPU, dix en rosace autour de l'origine ; les
// jambes sont les arêtes du losange, la base une diagonale jamais tracée.
// Les arcs de Conway (deux par losange, centrés aux sommets aigus du fin, aux
// sommets de 72° de l'épais, rayons 1/φ et 1/φ²) se raccordent à travers tout
// le pavage en courbes continues : c'est la règle de raccord elle-même
// (vérifié par tools/test-penrose.mjs).
//
// LE ZOOM INFINI, le détail décisif. Le zoom logarithmique avance d'un
// niveau (un facteur φ) par mesure. On affiche toujours le niveau FIN m, en
// unités où ses jambes font 1 ; z' = φ^u (u : la position dans la mesure)
// est l'échelle à l'écran. Chaque côté d'un triangle sait s'il est porté par
// une arête du niveau précédent (ANCIEN) ou non (NOUVEAU) :
//   jambes anciennes : toujours tracées (ce sont des arêtes des deux niveaux)
//   jambes nouvelles : naissent fines et s'épaississent (poids w_in(u))
//   bases anciennes  : des arêtes du niveau précédent devenues diagonales :
//                      restent un instant en surbrillance puis s'effacent (w_out(u))
//   arcs : fondu des arcs du parent (portés par chaque enfant) vers les siens
//   remplissage : fondu de la teinte du parent vers la sienne
// À u = 0 l'image est exactement celle du niveau m - 1 à u = 1 : le passage
// d'un niveau au suivant est invisible. Les coordonnées sont multipliées par
// φ à chaque niveau et les triangles hors du cercle de vue abandonnés : la
// précision ne dérive jamais, le zoom est sans fin (le niveau absolu n'est
// jamais calculé : φ^1800 déborderait après une heure). Un seul tampon,
// réécrit une fois par mesure (~2 000 à 8 000 triangles selon l'écran).
//
// LE RENDU : un triangle GPU par triangle de Robinson ; le fragment reçoit
// A, B, C, les arcs (les siens et ceux du parent) et calcule les DISTANCES en
// pixels (R1 : coeur 0-2 px blanc chaud au-dessus du seuil de bloom, gaine
// couleur, halo court). Remplissage très sombre teinté par type et par l'une
// des cinq orientations du losange, dix nuances (R3 : jamais de gris, des noirs colorés). Les traits
// s'effacent par taille en pixels (aucun moiré dans les petites tuiles).
// Tier 0 : arcs seuls, sans remplissage teinté.
//
// LA MUSIQUE (party) : TEMPS - une onde part du centre et allume les courbes
// de Conway sur son passage, séparation RGB (aberration pulsée) ; bands.high -
// les tuiles fines scintillent ; les FLEURS (soleils : cinq losanges épais,
// détectés au build) s'illuminent sur les temps forts ; MESURE - un niveau de
// zoom, à-coup de caméra ; PHRASE - rotation de 36° (continue, un dixième de
// tour par phrase), palette suivante ; MONTÉE - zoom accéléré (φ par temps),
// traits plus fins ; DROP - flash (confort), le pavage se dégonfle d'un niveau
// en un temps (zoom arrière : on réaffiche les parents), puis reprend ;
// SCÈNE - au début de chaque cycle, 36° de plus et une palette.
//
// Debug : ?penlevel=<x> (zoom figé à x niveaux, fraction = position dans le
// fondu), ?penarcs=0|1, ?penfreeze=1 (zoom, rotation et ondes figés),
// ?pendebug=1 (côtés colorés par drapeau : vert ancienne jambe, rouge nouvelle, bleu ancienne
// base), 2 (remplissages seuls, amplifiés), 5 (arêtes seules avec leurs poids),
// ?penrot=<deg> (rotation fixe ajoutée : la symétrie d'ordre 5 se vérifie en comparant 0 et 72),
// ?penprobe=1 (window.__penrose). penfreeze fige aussi l'à-coup de caméra, les fleurs et le
// scintillement : un pavage statique, pour les mesures de symétrie et de netteté.

const L0_FRAC = 0.042, L0_MIN = 28, L0_MAX = 50;       // (à 24-34 px les arcs de Conway n'étaient que des traits)       // pixels par jambe (niveau fin) au début de la mesure ; les arêtes tracées font φ fois plus
const MAX_TRIS = 16384;
const FLOATS = 26;                                    // par sommet
const NO_BPM = 128;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const SCENE_S = 48;
const SAFE_FLOOR = 0.4;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function smooth(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}

// les fondus du niveau, fonctions de u (réversibles : le drop remonte le temps)
export function levelWeights(u) {
  return {
    newEdge: smooth((u - 0.02) / 0.63),
    oldBase: 1 - smooth((u - 0.15) / 0.45),
    // (part de 0 : à u = 0 l'image doit être exactement celle de la fin de la mesure précédente)
    highlight: 0.35 * smooth(u / 0.06) * (1 - smooth((u - 0.12) / 0.35)),
    arcNew: smooth((u - 0.15) / 0.6),
    fill: smooth((u - 0.1) / 0.7)
  };
}

// ------------------------------------------------------------------ GLSL
const TILE_VERTEX = `
  attribute vec2 aPos;
  attribute vec2 aA, aB, aC;
  attribute vec3 aArc1, aArc2, aPArc1, aPArc2;
  attribute vec4 aMeta;            // drapeaux des côtés (AB + 2 AC + 4 BC), type + 2 typeParent, orientation + 16 orientParent, fleur
  attribute vec2 aFlower;
  uniform float uRot;
  uniform float uPx;               // pixels par unité de niveau
  uniform vec2 uRes;
  uniform vec2 uCenterPx;
  varying vec2 vPos;
  varying vec2 vA, vB, vC;
  varying vec3 vArc1, vArc2, vPArc1, vPArc2;
  varying vec4 vMeta;
  varying vec2 vFlower;
  void main() {
    float c = cos(uRot), s = sin(uRot);
    vec2 r = vec2(c * aPos.x - s * aPos.y, s * aPos.x + c * aPos.y) * uPx + uCenterPx;
    gl_Position = vec4(r / (uRes * 0.5), 0.0, 1.0);
    vPos = aPos; vA = aA; vB = aB; vC = aC;
    vArc1 = aArc1; vArc2 = aArc2; vPArc1 = aPArc1; vPArc2 = aPArc2; vMeta = aMeta; vFlower = aFlower;
  }
`;
const TILE_FRAGMENT = `
  precision highp float;
  uniform float uPx;
  uniform float uRot;
  uniform vec2 uCenterPx;
  uniform vec2 uRes;
  uniform float uNewEdge, uOldBase, uHighlight, uArcNew, uFillMix;
  uniform float uWidth;            // largeur du coeur, px (la montée l'affine)
  uniform vec3 uEdgeCol, uArcCol;
  uniform vec3 uWave;              // rayon (px), largeur (px), force
  uniform float uHigh, uTime;
  uniform vec2 uFlowerSel;         // fenêtre de hachage (centre, demi-largeur) ; enveloppe
  uniform float uFlowerEnv, uFlowerAll;
  uniform float uArcsOn, uTier0, uGain;
  uniform vec2 uFillHue;           // teinte de base des remplissages, luminance (linéaire)
  uniform float uDebug;            // 1 : côtés colorés par drapeau ; 2 : remplissages seuls, amplifiés
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vPos;
  varying vec2 vA, vB, vC;
  varying vec3 vArc1, vArc2, vPArc1, vPArc2;
  varying vec4 vMeta;
  varying vec2 vFlower;
  float segDist(vec2 p, vec2 a, vec2 b) { vec2 ab = b - a, ap = p - a; float h = clamp(dot(ap, ab) / dot(ab, ab), 0.0, 1.0); return length(ap - ab * h); }
  float hash(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  // le néon (R1), version RÉSEAU DENSE : coeur blanc chaud d'un pixel et demi (au-dessus
  // du seuil de bloom), gaine couleur étroite (lum 0,6), halo court et faible - des
  // arêtes tous les 40 px, une gaine de 6 px et un halo de 10 px blanchissaient tout
  // l'écran (luminance 0,78) ; un trait plus fin qu'un pixel garde un pixel et perd de l'intensité
  vec3 neon(float dpx, float w, vec3 col, float amp) {
    float wc = max(w, 1.0), k = min(1.0, w) * amp;
    float core = 1.0 - smoothstep(wc * 0.5, wc * 0.5 + 0.9, dpx);
    float sheath = 1.0 - smoothstep(wc * 0.5 + 0.2, wc * 0.5 + 1.2, dpx);
    float halo = exp(-dpx / 5.0) * 0.03;
    // (coeur teinté : sur un réseau dense, des coeurs blancs rendaient tout gris-blanc)
    return k * (mix(col, vec3(1.0, 0.95, 0.9), 0.45) * 1.15 * core + col * (0.5 * sheath + halo));
  }
  vec3 hsl(float h, float sat, float l) {
    vec3 k = mod(h * 6.0 + vec3(0.0, 4.0, 2.0), 6.0);
    vec3 f = clamp(min(k, 4.0 - k), 0.0, 1.0);
    return l + sat * (f - 0.5) * (1.0 - abs(2.0 * l - 1.0));
  }
  // la teinte d'un losange : type (0 fin, 1 épais) et orientation (0..4) -> dix noirs colorés
  // (calculée ici plutôt que lue dans un tableau d'uniforms indexé : sur ANGLE/Metal, l'index
  // dynamique dans le vertex shader donnait des teintes fausses et des tuiles noires)
  vec3 tint(float t, float o) { return hsl(fract(uFillHue.x + (o - 2.0) * 0.05 + t * 0.08), 0.9, uFillHue.y * (1.0 + t)); }
  float safeAtt(vec2 scr) {
    vec2 s = scr / (uRes.y * 0.5);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  void main() {
    vec2 p = vPos;
    float px = uPx;
    // la taille des tuiles à l'écran : les traits s'effacent sous ~8 px (aucun moiré)
    float sizeFade = smoothstep(6.0, 14.0, px);
    // (les varyings constants par triangle s'interpolent à l'arrondi près : on les arrondit)
    float mx = floor(vMeta.x + 0.5);
    float fAB = mod(mx, 2.0), fAC = mod(floor(mx / 2.0), 2.0), fBC = floor(mx / 4.0);
    float dAB = segDist(p, vA, vB) * px, dAC = segDist(p, vA, vC) * px, dBC = segDist(p, vB, vC) * px;
    float wOld = 1.0 + uHighlight;
    vec3 col = vec3(0.0);
    // LES ARÊTES : les jambes (anciennes : pleines ; nouvelles : naissent fines), la base ancienne qui s'efface
    col += neon(dAB, uWidth * mix(uNewEdge, 1.0, fAB), uEdgeCol, mix(uNewEdge, wOld, fAB));
    col += neon(dAC, uWidth * mix(uNewEdge, 1.0, fAC), uEdgeCol, mix(uNewEdge, wOld, fAC));
    col += neon(dBC, uWidth, uEdgeCol, fBC * uOldBase * wOld);
    col *= sizeFade;
    // LES ARCS DE CONWAY : les siens (fondu entrant), ceux du parent (sortant) ; l'onde du temps les allume
    if (uArcsOn > 0.5) {
      float wave = exp(-pow((length(p) * px - uWave.x) / uWave.y, 2.0)) * uWave.z;
      float boost = 1.0 + 3.0 * wave;
      vec3 arcs = vec3(0.0);
      float aw = uWidth * 0.9;
      if (vArc1.z > 0.0) arcs += neon(abs(length(p - vArc1.xy) - vArc1.z) * px, aw, uArcCol, uArcNew);
      if (vArc2.z > 0.0) arcs += neon(abs(length(p - vArc2.xy) - vArc2.z) * px, aw, uArcCol, uArcNew);
      if (vPArc1.z > 0.0) arcs += neon(abs(length(p - vPArc1.xy) - vPArc1.z) * px, aw, uArcCol, 1.0 - uArcNew);
      if (vPArc2.z > 0.0) arcs += neon(abs(length(p - vPArc2.xy) - vPArc2.z) * px, aw, uArcCol, 1.0 - uArcNew);
      col += arcs * boost * sizeFade;
    }
    // LE REMPLISSAGE : très sombre, teinte du parent -> la sienne ; les tuiles fines scintillent sur les aigus
    float my = floor(vMeta.y + 0.5), mz = floor(vMeta.z + 0.5);
    vec3 vFill = tint(mod(my, 2.0), mod(mz, 16.0)), vPFill = tint(floor(my / 2.0), floor(mz / 16.0));
    if (uTier0 < 0.5) {
      vec3 fill = mix(vPFill, vFill, uFillMix);
      float thin = 1.0 - mod(my, 2.0);
      // (rare : un losange fin sur six, sinon le fond grisait ; clé = le milieu de la base,
      // commun aux deux moitiés : le losange entier scintille, pas un triangle)
      float hs = hash(floor((vB + vC) * 3.5) + floor(uTime * 14.0));
      float twinkle = thin * uHigh * step(0.84, hs) * (hs - 0.84) / 0.16;
      col += fill * (1.0 + 2.5 * twinkle) + uArcCol * 0.05 * twinkle;
    }
    // LES FLEURS : cinq losanges épais autour d'un soleil, allumées sur les temps forts
    if (vMeta.w > 0.5) {
      float h = hash(floor(vFlower * 5.0) + 0.5);
      float sel = max(uFlowerAll, step(abs(fract(h - uFlowerSel.x + 0.5) - 0.5), uFlowerSel.y));
      float g = smoothstep(1.15, 0.15, length(p - vFlower)) * sel * uFlowerEnv;
      col += vec3(1.0, 0.85, 0.6) * g * 0.55 + uArcCol * g * 0.25;
    }
    if (uDebug > 0.5 && uDebug < 1.5) {
      col = vec3(0.0);
      col += neon(dAB, 1.5, mix(vec3(1, 0, 0), vec3(0, 1, 0), fAB), 1.0);
      col += neon(dAC, 1.5, mix(vec3(1, 0, 0), vec3(0, 1, 0), fAC), 1.0);
      col += neon(dBC, 1.5, vec3(0, 0.4, 1), fBC);
    } else if (uDebug > 1.5 && uDebug < 2.5) col = mix(vPFill, vFill, uFillMix) * 12.0;
    else if (uDebug > 2.5 && uDebug < 3.5) col = vec3(mod(my, 2.0), mod(mz, 16.0) / 5.0, floor(mz / 16.0) / 5.0);
    else if (uDebug > 4.5) { col = vec3(0.0); col += neon(dAB, uWidth * mix(uNewEdge, 1.0, fAB), uEdgeCol, mix(uNewEdge, wOld, fAB)); col += neon(dAC, uWidth * mix(uNewEdge, 1.0, fAC), uEdgeCol, mix(uNewEdge, wOld, fAC)); col += neon(dBC, uWidth, uEdgeCol, fBC * uOldBase * wOld); }
    vec2 scr = vec2(cos(uRot) * p.x - sin(uRot) * p.y, sin(uRot) * p.x + cos(uRot) * p.y) * px + uCenterPx;
    col *= safeAtt(scr) * uGain;
    gl_FragColor = vec4(col, 1.0);
  }
`;
const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const DISPLAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;
const LUM_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { vec3 c = texture2D(uTex, vUv).rgb; gl_FragColor = vec4(vec3(pow(dot(c, vec3(0.2126, 0.7152, 0.0722)), 1.0 / 2.2)), 1.0); }
`;

// =============================================================================

export class PenroseVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    const pl = parseFloat(urlParam('penlevel'));
    this._forceLevel = Number.isFinite(pl) && pl >= 0 ? pl : null;
    this._arcsOn = urlParam('penarcs') !== '0';
    this._freeze = urlParam('penfreeze') === '1';
    const pr = parseFloat(urlParam('penrot'));
    this._debugRot = Number.isFinite(pr) ? pr * Math.PI / 180 : 0;
    // (bloom bien plus discret que le preset : des milliers de coeurs ; à 0,6 puis 0,3 le bloom
    // doublait la luminance de l'image (0,24 mesuré avant lui, 0,52 après) en voilant le noir)
    this._post = new VdjPost({ bloomStrength: 0.12, bloomThreshold: 0.85, grain: 0.015, vignette: 0.45, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._postValue = this._post.update(1);
    // le confort : un gain ABSOLU (plafond / luminance mesurée au gain 1), lissé - le gain
    // multiplicatif de VdjComfort s'effondrait à 0,04 pendant que sa moyenne lente restait haute ;
    // mesuré AVANT la chaîne partagée (bloom, filmic : +40 % mesurés) : plafond interne 0,27 pour < 0,45 à l'écran
    this._comfort = new VdjComfort();
    this._lumAvg = 0.2; this._gain = 1; this._gainRendered = 1;
    if (urlParam('penprobe') === '1' && typeof window !== 'undefined') window.__penrose = this;
  }

  get postProcessing() { return this._postValue; }

  init(scene) {
    this._sceneRef = scene;
    this._size = new THREE.Vector2(1280, 720);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    // le tampon des tuiles : réécrit une fois par mesure
    this._data = new Float32Array(MAX_TRIS * 3 * FLOATS);
    const ib = new THREE.InterleavedBuffer(this._data, FLOATS);
    ib.setUsage(THREE.DynamicDrawUsage);
    this._ib = ib;
    const g = new THREE.BufferGeometry();
    let off = 0;
    const attr = (name, n) => { g.setAttribute(name, new THREE.InterleavedBufferAttribute(ib, n, off)); off += n; };
    attr('aPos', 2); attr('aA', 2); attr('aB', 2); attr('aC', 2); attr('aArc1', 3); attr('aArc2', 3); attr('aPArc1', 3); attr('aPArc2', 3); attr('aMeta', 4); attr('aFlower', 2);
    g.setAttribute('position', new THREE.InterleavedBufferAttribute(ib, 2, 0));   // (three veut un attribut position)
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    g.setDrawRange(0, 0);
    this._geo = g;
    this._U = {
      uRot: { value: 0 }, uPx: { value: 30 }, uRes: { value: this._size }, uCenterPx: { value: new THREE.Vector2() },
      uFillHue: { value: new THREE.Vector2(0.4, 0.006) },
      uNewEdge: { value: 0 }, uOldBase: { value: 1 }, uHighlight: { value: 0 }, uArcNew: { value: 0 }, uFillMix: { value: 0 },
      uWidth: { value: 2.0 }, uEdgeCol: { value: new THREE.Vector3(1, 0.3, 0.8) }, uArcCol: { value: new THREE.Vector3(0.3, 0.9, 1) },
      uWave: { value: new THREE.Vector3(-1000, 40, 0) }, uHigh: { value: 0 }, uTime: { value: 0 },
      uFlowerSel: { value: new THREE.Vector2(0.5, 0.2) }, uFlowerEnv: { value: 0 }, uFlowerAll: { value: 0 },
      uArcsOn: { value: this._arcsOn ? 1 : 0 }, uTier0: { value: this._tier === 0 ? 1 : 0 }, uGain: { value: 1 }, uDebug: { value: parseFloat(urlParam('pendebug')) || 0 },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    // DoubleSide : les deux moitiés d'un losange sont des triangles miroirs, enroulés dans les deux
    // sens ; en FrontSide la moitié du pavage était éliminée (arêtes manquantes, tuiles noires)
    this._mesh = new THREE.Mesh(g, new THREE.ShaderMaterial({ uniforms: this._U, vertexShader: TILE_VERTEX, fragmentShader: TILE_FRAGMENT, depthTest: false, depthWrite: false, side: THREE.DoubleSide }));
    this._mesh.frustumCulled = false;
    this._tScene = new THREE.Scene(); this._tScene.add(this._mesh);
    this._target = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false });
    this._dispU = { uTex: { value: this._target.texture } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    this._display.onBeforeRender = (renderer) => this._render(renderer);
    scene.add(this._display);
    // la mesure de confort (R10) : 16 x 16 texels relus deux fois par seconde
    this._lumTarget = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false });
    this._lumPixels = new Uint8Array(16 * 16 * 4);
    this._lumU = { uTex: { value: this._target.texture } };
    this._lumScene = new THREE.Scene();
    const lq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._lumU, vertexShader: QUAD_VERTEX, fragmentShader: LUM_FRAGMENT, depthTest: false, depthWrite: false }));
    lq.frustumCulled = false; this._lumScene.add(lq);
    this._lumAge = 0; this._lumMeasured = 0.2; this._lumPending = false;

    // l'état
    this._time = 0; this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._paused = false;
    this._logZ = 0; this._dropShift = 0; this._dropBase = 0; this._dropAt = null; this._level = 0;     // niveau relatif : m = floor(logZ + dropShift)
    this._rot = 0; this._rotExtra = 0; this._rotExtraFrom = 0; this._rotExtraAt = -99;
    this._phraseN = 0; this._palIdx = 0; this._hues = [0.85, 0.5];
    this._beatAt = -99; this._barAt = -99; this._high = 0; this._build = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null;
    this._sceneClock = new SceneClock({ period: SCENE_S, start: 0 });
    this._sceneRef = scene;
    this._L0 = 26; this._viewR = 40; this._tiles = null; this._parents = null; this._count = 0;
    this._paletteSeen = undefined;
    this._stats = { beats: 0, bars: 0, phrases: 0, drops: 0, levelsUp: 0, levelsDown: 0, rebuilds: 0, uploads: 0 };
    this._applyPalette(null);
  }

  // ------------------------------------------------------------ le pavage
  _ensureTiling(w, h) {
    const H = h, R = Math.hypot(w, h) / 2;
    let L0 = Math.min(L0_MAX, Math.max(L0_MIN, L0_FRAC * H));
    L0 = Math.max(L0, R * Math.sqrt(8.3 / (MAX_TRIS * 0.95)));       // jamais plus de MAX_TRIS triangles (mesuré : 8,3 triangles par unité² de vue)
    const viewR = R / L0;
    if (this._tiles && Math.abs(L0 - this._L0) < 1e-6 && Math.abs(viewR - this._viewR) < 1e-6) return;
    this._L0 = L0; this._viewR = viewR;
    // assez de déflations pour que le disque de la rosace (rayon φ^n) couvre la vue
    const n = Math.max(2, Math.ceil(Math.log(viewR * 1.15) / Math.log(PHI)));
    this._parents = buildTiling(n - 1, viewR);
    this._tiles = subdivide(this._parents, PHI, withinRadius(viewR));
    this._stats.rebuilds++;
    this._upload();
  }
  _levelUp() {
    const next = subdivide(this._tiles, PHI, withinRadius(this._viewR));
    this._parents = this._tiles; this._tiles = next;
    this._stats.levelsUp++;
    this._upload();
  }
  _levelDown() {
    if (!this._parents) return false;
    this._tiles = this._parents; this._parents = null;
    this._stats.levelsDown++;
    this._upload();
    return true;
  }
  _upload() {
    const tris = this._tiles, D = this._data;
    const fl = flowers(tris);
    const n = Math.min(MAX_TRIS, tris.length);
    let k = 0;
    for (let i = 0; i < n; i++) {
      const tr = tris[i], p = tr.parent;
      // (les rayons du parent sont donnés pour une jambe de 1 : ses coordonnées ont été multipliées par φ)
      const f = sideFlags(tr), [a1, a2] = arcsOf(tr), [p1, p2] = p ? arcsOf(p).map(([c, r]) => [c, r * PHI]) : [[[0, 0], 0], [[0, 0], 0]];
      const meta = [f[0] + 2 * f[1] + 4 * f[2], tr.t + 2 * (p ? p.t : tr.t), orientationBin(tr) + 16 * (p ? orientationBin(p) : orientationBin(tr)), fl[i] ? 1 : 0];
      const fc = fl[i] || [0, 0];
      for (const V of [tr.A, tr.B, tr.C]) {
        D[k++] = V[0]; D[k++] = V[1];
        D[k++] = tr.A[0]; D[k++] = tr.A[1]; D[k++] = tr.B[0]; D[k++] = tr.B[1]; D[k++] = tr.C[0]; D[k++] = tr.C[1];
        D[k++] = a1[0][0]; D[k++] = a1[0][1]; D[k++] = a1[1]; D[k++] = a2[0][0]; D[k++] = a2[0][1]; D[k++] = a2[1];
        D[k++] = p1[0][0]; D[k++] = p1[0][1]; D[k++] = p1[1]; D[k++] = p2[0][0]; D[k++] = p2[0][1]; D[k++] = p2[1];
        D[k++] = meta[0]; D[k++] = meta[1]; D[k++] = meta[2]; D[k++] = meta[3];
        D[k++] = fc[0]; D[k++] = fc[1];
      }
    }
    this._count = n; this._flowerCount = fl.count;
    this._ib.clearUpdateRanges(); this._ib.addUpdateRange(0, k); this._ib.needsUpdate = true;
    this._geo.setDrawRange(0, n * 3);
    this._stats.uploads++;
  }

  // ------------------------------------------------------------ la palette
  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2bd6, 0x2bd8ff];
    if (pal === this._paletteSeen && this._paletteSeen !== undefined) return;
    this._paletteSeen = pal;
    const hsl = { h: 0, s: 0, l: 0 };
    const h1 = vdjColor(v[0]).getHSL(hsl).h;
    let h2 = vdjColor(v[1 % v.length]).getHSL(hsl).h;
    let dh = ((h2 - h1) % 1 + 1) % 1; if (dh > 0.5) dh -= 1;
    if (Math.abs(dh) < 0.25) h2 = h1 + (dh >= 0 ? 0.33 : -0.33);     // R4 : 0,25 de teinte d'écart au moins
    this._hues = [h1, ((h2 % 1) + 1) % 1];
    this._setPalette(this._palIdx);
  }
  // la palette k : les arêtes sur une teinte, les arcs sur l'autre, dix nuances de remplissage très sombres
  _setPalette(k) {
    const [h1, h2] = this._hues;
    const variants = [[h1, h2], [h2, h1], [h1 + 0.08, h2 - 0.08], [h2 + 0.5, h1 + 0.5]];
    const [he, ha] = variants[k % variants.length];
    const c = new THREE.Color();
    c.setHSL(((he % 1) + 1) % 1, 0.9, 0.55); this._U.uEdgeCol.value.set(c.r, c.g, c.b);
    c.setHSL(((ha % 1) + 1) % 1, 0.9, 0.6); this._U.uArcCol.value.set(c.r, c.g, c.b);
    // les remplissages : la teinte complémentaire des arêtes, dix noirs colorés (R3), calculés dans le shader
    this._U.uFillHue.value.set(((he + 0.5) % 1 + 1) % 1, 0.006);
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._drops.sort((a, b) => a - b); this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0; while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) { if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true; this._dropIdx++; }
    return fired;
  }

  // ------------------------------------------------------------ la boucle
  update(deltaTime, profile) {
    this._paused = profile.playbackState != null && profile.playbackState !== 'PLAYING';
    this._applyPalette(profile.artworkPalette);
    if (this._paused) return;                     // pause : gel
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._time += dt;
    this._sceneClock.update(dt);

    // l'horloge en temps, recalée sur la grille de mesure
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? THREE.MathUtils.clamp(bpm, 60, 200) : NO_BPM;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > 2) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }
    const clock = this._clock, dClock = clock - this._clockPrev;
    const beatS = 60 / this._bpm;
    const beat = Math.floor(clock) !== Math.floor(this._clockPrev);
    const bar = Math.floor(clock / 4) !== Math.floor(this._clockPrev / 4);
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else phrase = Math.floor(clock / 16) !== Math.floor(this._clockPrev / 16);
    const barPhase = clock / 4 - Math.floor(clock / 4), barIdx = Math.floor(clock / 4);

    const director = profile.director;
    const bands = director && director.bands;
    this._high += ((bands && bands.high > 0 ? clamp01(bands.high) : 0.3 + 0.3 * Math.sin(clock * 2.1)) - this._high) * (1 - Math.exp(-dt / 0.08));
    // la montée : un drop connu qui approche, ou le segment « build » : le zoom s'emballe (φ par temps)
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && (director.mode === 'build' || director.mode === 'buildup')) build = Math.max(build, 0.7);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 0.6));

    // LE DROP : flash, le pavage se dégonfle d'un niveau en un temps, puis reprend
    let drop = hasGrid && this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && this._dropAt == null) {
      this._dropAt = clock; this._stats.drops++;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
    }
    if (this._dropAt != null) {
      const u = (clock - this._dropAt);
      this._dropShift = this._dropBase - smooth(u);                     // -1 au bout d'un temps
      if (u >= 1) { this._dropBase = this._dropShift; this._dropAt = null; }
    } else this._dropBase = this._dropShift;

    // LE ZOOM : un niveau par mesure (φ), x4 en montée ; figé en debug
    if (!this._freeze && this._forceLevel == null) this._logZ += dClock / 4 * (1 + 3 * this._build);
    const logZ = this._forceLevel != null ? this._forceLevel : this._logZ + this._dropShift;
    const m = Math.floor(logZ), u = logZ - m;
    if (this._tiles) {
      while (m > this._level) { this._levelUp(); this._level++; }
      while (m < this._level) { if (!this._levelDown()) { this._level = m; break; } this._level--; }
    }
    this._u = u;

    // LA ROTATION : 36° par phrase (continue), 36° de plus au début d'une scène, l'à-coup de caméra sur la mesure
    if (!this._freeze) this._rot += dClock / 16 * (Math.PI / 5);
    if (this._sceneClock.wrapped && !this._freeze) { this._rotExtraFrom = this._rotExtra; this._rotExtraAt = this._time; this._rotExtra += Math.PI / 5; this._palIdx++; this._setPalette(this._palIdx); }
    const rotExtra = lerp(this._rotExtraFrom, this._rotExtra, smooth((this._time - this._rotExtraAt) / 1.5));
    const kick = hasGrid && !this._freeze ? vdjCameraKick(barPhase, barIdx, 4) : { zoom: 1, roll: 0 };

    // TEMPS : l'onde des courbes ; MESURE : les fleurs ; PHRASE : la palette suivante
    if (beat) { this._beatAt = clock; this._stats.beats++; }
    if (bar) { this._barAt = clock; this._stats.bars++; this._U.uFlowerSel.value.set((barIdx * 0.618034) % 1, 0.22); }
    if (phrase) { this._phraseN++; this._stats.phrases++; this._palIdx++; this._setPalette(this._palIdx); }
    const sinceBeat = (clock - this._beatAt) * beatS, sinceBar = (clock - this._barAt) * beatS;
    const R = Math.hypot(this._size.x, this._size.y) / 2;
    const U = this._U;
    const waveR = this._freeze ? -1000 : sinceBeat / (beatS * 0.75) * R;
    U.uWave.value.set(waveR, Math.max(24, R * 0.035), hasGrid ? 1 : 0.5);
    U.uFlowerEnv.value = this._freeze ? 0 : Math.exp(-sinceBar / 0.45) * (hasGrid ? 1 : 0.6);
    U.uFlowerAll.value = this._dropAt != null || (this._dropShift < this._dropBase - 0.01) ? 1 : 0;
    U.uHigh.value = this._freeze ? 0 : this._high;
    U.uTime.value = this._time;
    U.uWidth.value = lerp(1.5, 0.9, this._build);
    const W = levelWeights(u);
    U.uNewEdge.value = W.newEdge; U.uOldBase.value = W.oldBase; U.uHighlight.value = W.highlight; U.uArcNew.value = W.arcNew; U.uFillMix.value = W.fill;
    U.uRot.value = this._rot + rotExtra + kick.roll + this._debugRot;
    this._zPrime = Math.pow(PHI, u) * kick.zoom;
    // R10 : le confort mesuré
    this._lumAge += dt;
    if (this._lumAge >= 0.5) { this._lumAge = 0; this._lumPending = true; }
    this._lumAvg += (this._lumMeasured - this._lumAvg) * Math.min(1, dt / 1.0);
    const gt = Math.min(1, 0.27 / Math.max(1e-3, this._lumAvg));
    this._gain += (gt - this._gain) * Math.min(1, dt / 0.5);
    U.uGain.value = this._gain;
    this._postValue = this._post.update(hasGrid ? vdjBeatChroma(clock - Math.floor(clock), beatS) : 1);
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    if (prev) this._size.set(prev.width, prev.height); else renderer.getDrawingBufferSize(this._size);
    const w = Math.max(16, Math.round(this._size.x)), h = Math.max(16, Math.round(this._size.y));
    if (this._target.width !== w || this._target.height !== h) this._target.setSize(w, h);
    this._ensureTiling(w, h);
    const U = this._U;
    U.uPx.value = this._L0 * (this._zPrime || 1);
    const sc = safeZoneUniforms.uStageCenter.value;
    U.uCenterPx.value.set(sc.x * h / 2, sc.y * h / 2);
    if (!this._paused || !this._rendered) {
      this._gainRendered = this._gain;
      renderer.setRenderTarget(this._target);
      renderer.setClearColor(0x000000, 1);
      renderer.clear(true, false, false);
      renderer.render(this._tScene, this._ortho);
      this._rendered = true;
      if (this._lumPending) {
        this._lumPending = false;
        renderer.setRenderTarget(this._lumTarget);
        renderer.render(this._lumScene, this._ortho);
        renderer.readRenderTargetPixels(this._lumTarget, 0, 0, 16, 16, this._lumPixels);
        let sum = 0; for (let i = 0; i < this._lumPixels.length; i += 4) sum += this._lumPixels[i];
        this._lumMeasured = sum / (256 * 255) / Math.max(0.05, this._gainRendered);   // ramenée au gain 1
      }
    }
    renderer.setRenderTarget(prev);
  }

  getProbe() {
    return { tier: this._tier, L0: +this._L0.toFixed(1), viewR: +this._viewR.toFixed(1), triangles: this._count, flowers: this._flowerCount || 0, level: this._level, u: +(this._u || 0).toFixed(3),
      logZ: +this._logZ.toFixed(3), dropShift: +this._dropShift.toFixed(3), rot: +(this._U.uRot.value).toFixed(3), lum: +this._lumMeasured.toFixed(3), gain: +this._gain.toFixed(3), lumAvg: +this._lumAvg.toFixed(3),
      build: +this._build.toFixed(2), stats: Object.assign({}, this._stats) };
  }

  dispose() {
    if (this._sceneRef && this._display) { this._display.onBeforeRender = null; this._sceneRef.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._mesh) { this._mesh.material.dispose(); this._geo.dispose(); }
    if (this._target) this._target.dispose();
    if (this._lumTarget) this._lumTarget.dispose();
    if (typeof window !== 'undefined' && window.__penrose === this) window.__penrose = null;
    this._sceneRef = null;
  }
}
